import { Trans } from "react-i18next";
import Pencil from "../images/pencil.svg?react";
import styles from "./index.module.css";
import { Typography, Button, TypographyVariant } from "@mui/material";
import { proctoringConfigurationToMethodName } from "@verity/proctoring-option";
import {
	OptionRelatedInstituteSettings,
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";
import { EditQuizStep, getProctoringMethodName } from "../proctoring-options/option-text/ProctoringOptionText";
import ConfigurationSummaryList, { ConfigurationSummaryListProps } from "./ConfigurationSummaryList";
import ThirdPartyStepIcon from "../images/confirmation/third-party-step.svg?react";
import AccommodationStepIcon from "../images/confirmation/accommodation-step.svg?react";
import ConfigurationStepIcon from "../images/confirmation/configuration-step.svg?react";
import PlatformStepIcon from "../images/confirmation/platform-step.svg?react";
import ProfileDetailsIcon from "../images/confirmation/profile-details.svg?react";
import AccommodationDetailsIcon from "../images/confirmation/accommodation-details.svg?react";
import AccommodationNotesIcon from "../images/confirmation/accommodation-notes.svg?react";
import LiveSessionIcon from "../images/confirmation/live-session.svg?react";
import IncompleteIcon from "../images/incomplete-icon.svg?react";
import AssessmentDetailsIcon from "../images/confirmation/assessment-details.svg?react";
import AddInstructionIcon from "../images/confirmation/add-instruction.svg?react";
import { EditQuizPageProps } from "../editQuiz";
import { ConfirmationPageProps } from "../edit-quiz-steps/ConfirmationStep";
import { spansMultipleDays } from "../live/liveHelper";
import {
	CoreFileCard,
	CoreFileItem,
	CoreIconButton,
	CoreRadioCardIcon,
	CoreSkeletonWrapper,
	EllipsisTooltip,
	OverflowText,
} from "../../core";
import { getProfileName } from "../profile";
import { getUIConfigForProfile } from "../profile/profileHelpers";
import { METHOD_ICON_CONFIG } from "../edit-quiz-steps/StepIconConfig";
import { VerityInstituteSettings } from "../../types/common";
import { useMemo, isValidElement } from "react";
import { categorizeProctoringOptions } from "./confirmationHelper";
import { VerityAssessmentType } from "@verity/database/prisma";

type ConfirmationRowProps = {
	title?: React.ReactNode;
	description?: React.ReactNode;
	/**@default undefined Which tooltip to use on overflow. Overflow -> OverflowText (light), Ellipsis -> EllipsisTooltip (has scrollbars but clones in dom) */
	tooltipType?: "overflow" | "ellipsis";
};

function ConfirmationRow({ description, title, tooltipType }: ConfirmationRowProps) {
	const renderWrapper = (children: React.ReactNode, className: string) => {
		switch (tooltipType) {
			case "overflow":
				return <OverflowText className={className}>{children}</OverflowText>;
			case "ellipsis":
				if (isValidElement(children)) {
					return <EllipsisTooltip entry={children}>{children}</EllipsisTooltip>;
				}
				return (
					<EllipsisTooltip entry={children}>
						<span className={className}>{children}</span>
					</EllipsisTooltip>
				);
			default:
				return <Typography className={className}>{children}</Typography>;
		}
	};

	return (
		<div className={styles.descriptionRow}>
			{typeof title === "string" ? renderWrapper(title, styles.name) : title}
			{typeof description === "string" ? renderWrapper(description, styles.description) : description}
		</div>
	);
}

type ConfirmationEditButtonProps = {
	onEdit: () => void;
	targetPageName: EditQuizStep;
	hasText?: boolean;
};

export function ConfirmationEditButton({ targetPageName, onEdit, hasText = true }: ConfirmationEditButtonProps) {
	const commonProps = {
		onClick: onEdit,
		"aria-label": i18nPackage.t("editQuizPage.confirmationBlockTSX.editButtonAriaLabel", {
			page: targetPageName.toLowerCase(),
		}),
	};

	if (hasText)
		return (
			<Button {...commonProps} className={styles.editButton} startIcon={<Pencil />}>
				{i18nPackage.t("editQuizPage.confirmationBlockTSX.editButtonLabel")}
			</Button>
		);

	return (
		<CoreIconButton {...commonProps} variant="plain" className={styles.editButtonIcon}>
			<Pencil />
		</CoreIconButton>
	);
}

function ConfirmationBlockSkeleton() {
	return <ConfirmationBlock loading={true} />;
}

type ConfirmationBlockProps = {
	title?: string;
	children?: React.ReactNode;
	onEdit?: () => void;
	targetPageName?: EditQuizStep;
	icon?: React.ReactNode;
	/**@default "h2" */
	titleVariant?: TypographyVariant;
	error?: boolean;
	showCheckmark?: boolean;
	loading?: boolean;
};

function ConfirmationBlock({
	children,
	targetPageName,
	title,
	onEdit,
	icon,
	titleVariant = "h2",
	error,
	showCheckmark = true,
	loading,
}: ConfirmationBlockProps) {
	return (
		<CoreSkeletonWrapper loading={loading} variant="rounded" height={150}>
			<div className={styles.confirmationContainer}>
				<div className={styles.headerRow}>
					<div className={styles.headerIconWrapper}>
						{icon && <CoreRadioCardIcon icon={icon} enabled showCheckmark={showCheckmark} />}
						{title && <Typography variant={titleVariant}>{title}</Typography>}
						{error && (
							<Typography component={"span"} className={styles.liveSessionWarning}>
								<IncompleteIcon />
								<Typography component={"span"}>
									{i18nPackage.t("editQuizPage.confirmationBlockTSX.incompleteLabel")}
								</Typography>
							</Typography>
						)}
					</div>
					{onEdit && targetPageName && <ConfirmationEditButton targetPageName={targetPageName} onEdit={onEdit} />}
				</div>
				{children}
			</div>
		</CoreSkeletonWrapper>
	);
}

type CommonConfirmationBlockProps = Pick<
	ConfirmationBlockProps,
	"onEdit" | "titleVariant" | "showCheckmark" | "loading"
>;

type ThirdPartyBlockProps = {
	thirdPartyOption: ProctoringAssessmentConfigurationV2["thirdPartyAssessmentOption"];
	thirdPartyPlatforms: ConfirmationPageProps["data"]["thirdPartyPlatforms"];
	quizName?: string;
} & CommonConfirmationBlockProps;

export function ThirdPartyBlock({ thirdPartyOption, thirdPartyPlatforms, quizName, ...props }: ThirdPartyBlockProps) {
	const platformId = thirdPartyOption?.thirdPartyPlatformId ? Math.max(0, thirdPartyOption.thirdPartyPlatformId) : 0;
	const platformName =
		thirdPartyPlatforms.find((p) => p.platformId === platformId)?.platformName ??
		i18nPackage.t("editQuizPage.confirmationBlockTSX.defaultPlatform");
	const hasPassword = !!thirdPartyOption?.password;
	const quizUrl = thirdPartyOption?.thirdPartyUrl;
	return (
		<ConfirmationBlock
			{...props}
			targetPageName="thirdParty"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.thirdPartyAssessmentDetails")}
			icon={<ThirdPartyStepIcon />}
		>
			{quizName && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentName")}
					description={quizName}
					tooltipType="overflow"
				/>
			)}
			{quizUrl && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentUrl")}
					description={quizUrl}
					tooltipType="overflow"
				/>
			)}
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.platform")} description={platformName} />

			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.passwordProtection")}
				description={i18nPackage.t(`editQuizPage.confirmationBlockTSX.${hasPassword ? "yes" : "no"}`)}
			/>
		</ConfirmationBlock>
	);
}

type MethodBlockProps = {
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
} & CommonConfirmationBlockProps;

export function MethodBlock({ proctoringAssessmentConfiguration, loading, ...props }: MethodBlockProps) {
	if (loading || !proctoringAssessmentConfiguration) {
		return <ConfirmationBlockSkeleton />;
	}

	const methodText = getProctoringMethodName(proctoringAssessmentConfiguration);
	const method = proctoringConfigurationToMethodName({
		isLivePopIn: !!proctoringAssessmentConfiguration.isLivePopIn,
		enablePostReview: !!proctoringAssessmentConfiguration.enablePostReview,
		isLiveProctored: !!proctoringAssessmentConfiguration.isLiveProctored,
	});
	const MethodIcon = METHOD_ICON_CONFIG[method];

	return (
		<ConfirmationBlock
			{...props}
			targetPageName="method"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.method")}
			icon={<MethodIcon />}
		>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.selected")} description={methodText} />
		</ConfirmationBlock>
	);
}

type PlatformBlockProps = CommonConfirmationBlockProps & {
	platformText?: string;
};

export function PlatformBlock({ platformText, loading, ...props }: PlatformBlockProps) {
	if (loading || platformText === undefined) {
		return <ConfirmationBlockSkeleton />;
	}
	return (
		<ConfirmationBlock
			{...props}
			targetPageName="platform"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.platform")}
			icon={<PlatformStepIcon />}
		>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.type")} description={platformText} />
		</ConfirmationBlock>
	);
}

type AccommodationBlockProps = CommonConfirmationBlockProps & {
	numExempted: number;
};

export function AccommodationBlock({ numExempted, ...props }: AccommodationBlockProps) {
	return (
		<ConfirmationBlock
			{...props}
			targetPageName="accommodations"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.accommodations")}
			icon={<AccommodationStepIcon />}
		>
			<ConfirmationRow
				description={
					<Typography className={styles.description}>
						{numExempted > 0 ? (
							<Trans
								i18n={i18nPackage}
								i18nKey={"editQuizPage.confirmationBlockTSX.numAccommodated"}
								values={{ count: numExempted }}
								components={{ b: <strong /> }}
							/>
						) : (
							i18nPackage.t("editQuizPage.confirmationBlockTSX.noAccommodations")
						)}
					</Typography>
				}
			/>
		</ConfirmationBlock>
	);
}

type ConfigurationBlockProps = Omit<CommonConfirmationBlockProps, "onEdit"> &
	Pick<ConfigurationSummaryListProps, "applications" | "extensions" | "onEdit"> & {
		verityInstituteSettings?: VerityInstituteSettings;
		optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
		proctoringOption?: Partial<ProctoringOption>;
	};

export function ConfigurationBlock({
	applications,
	extensions,
	onEdit,
	proctoringOption,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	loading,
	...props
}: ConfigurationBlockProps) {
	const configurationConfig = useMemo(() => {
		if (!proctoringOption) return null;
		return categorizeProctoringOptions(proctoringOption, verityInstituteSettings, optionRelatedInstituteSettings);
	}, [optionRelatedInstituteSettings, proctoringOption, verityInstituteSettings]);

	if (loading || !configurationConfig) {
		return <ConfirmationBlockSkeleton />;
	}

	return (
		<ConfirmationBlock
			{...props}
			targetPageName="configuration"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.configurations")}
			icon={<ConfigurationStepIcon />}
		>
			{Object.keys(configurationConfig.optionCategories).length > 0 ? (
				<ConfigurationSummaryList
					optionCategories={configurationConfig.optionCategories}
					optionChildren={configurationConfig.optionChildren}
					applications={applications}
					extensions={extensions}
					onEdit={onEdit}
				/>
			) : (
				<ConfirmationRow description={i18nPackage.t("editQuizPage.confirmationBlockTSX.noRestrictions")} />
			)}
		</ConfirmationBlock>
	);
}

type ProfileBlockProps = {
	profile: ProctoringOptionProfileV2;
} & CommonConfirmationBlockProps;

export function ProfileBlock({ profile, ...props }: ProfileBlockProps) {
	const profileConfig = getUIConfigForProfile(profile);
	if (!profileConfig) return;
	const { name } = profileConfig;
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.profileDetails")}
			icon={<ProfileDetailsIcon />}
			targetPageName="profile"
		>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.profileName")}
				description={getProfileName(profile)}
			/>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.profileType")} description={name} />
		</ConfirmationBlock>
	);
}

type AccommodationDetailsBlockProps = {
	name: string;
	type: "notProctored" | "customProfile";
} & CommonConfirmationBlockProps;

export function AccommodationDetailsBlock({ name, type, ...props }: AccommodationDetailsBlockProps) {
	const translationKey = type === "notProctored" ? "notProctored" : "modifiedProctoring";
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.accommodationDetails")}
			icon={<AccommodationDetailsIcon />}
		>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.testTaker")} description={name} />
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.accommodation")}
				description={i18nPackage.t(`editQuizPage.confirmationBlockTSX.${translationKey}`)}
			/>
		</ConfirmationBlock>
	);
}

type AccommodationNotesBlockProps = {
	notes?: string;
} & CommonConfirmationBlockProps;

export function AccommodationNotesBlock({ notes, ...props }: AccommodationNotesBlockProps) {
	const finalNotes = notes ?? i18nPackage.t("editQuizPage.confirmationBlockTSX.noNotes");
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.accommodationNotes")}
			icon={<AccommodationNotesIcon />}
		>
			<ConfirmationRow description={finalNotes} />
		</ConfirmationBlock>
	);
}

function formatLiveSessionDate(date: Date) {
	return date.toLocaleDateString([], { dateStyle: "long" });
}

function formatLiveSessionTime(time: Date) {
	return time.toLocaleTimeString([], { timeStyle: "short" });
}

type LiveSessionBlockProps = {
	proctoringAssessmentConfiguration: EditQuizPageProps["proctoringAssessmentConfiguration"];
	pendingLiveSessions: ConfirmationPageProps["pendingLiveSessions"];
} & CommonConfirmationBlockProps;

export function LiveSessionBlock({
	pendingLiveSessions,
	proctoringAssessmentConfiguration,
	loading,
	...props
}: LiveSessionBlockProps) {
	if (loading || !proctoringAssessmentConfiguration) {
		return <ConfirmationBlockSkeleton />;
	}

	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.liveSession")}
			targetPageName="liveSessions"
			icon={<LiveSessionIcon />}
			error={proctoringAssessmentConfiguration.isLiveProctored && pendingLiveSessions.length === 0}
		>
			{pendingLiveSessions.map(({ identifier, name, startTime, endTime }) => {
				const multipleDates = spansMultipleDays(startTime, endTime);
				return (
					<ConfirmationRow
						key={identifier}
						title={name}
						tooltipType="overflow"
						description={
							multipleDates
								? `${formatLiveSessionDate(startTime)} ${formatLiveSessionTime(startTime)} to ${formatLiveSessionDate(endTime)} ${formatLiveSessionTime(endTime)}`
								: `${formatLiveSessionDate(startTime)} ${formatLiveSessionTime(startTime)} to ${formatLiveSessionTime(endTime)}`
						}
					/>
				);
			})}
		</ConfirmationBlock>
	);
}

type QuizQuestionBlockProps = {
	totalQuestions: number;
	totalPoints: number;
	icon?: React.ReactNode;
} & CommonConfirmationBlockProps;

export function QuizQuestionBlock({ totalQuestions, totalPoints, icon, ...props }: QuizQuestionBlockProps) {
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.questions")}
			icon={icon}
			targetPageName="questions"
		>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.totalQuestions")}
				description={String(totalQuestions)}
			/>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.totalPoints")}
				description={String(totalPoints)}
			/>
		</ConfirmationBlock>
	);
}

type AssessmentTypeBlockProps = {
	type: VerityAssessmentType;
	icon?: React.ReactNode;
} & CommonConfirmationBlockProps;

export function AssessmentTypeBlock({ type, icon, ...props }: AssessmentTypeBlockProps) {
	const key = type.toLowerCase() as "assignment" | "quiz";
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentType")}
			icon={icon ?? <PlatformStepIcon />}
			targetPageName="assessmentType"
		>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.type")}
				description={i18nPackage.t(`editQuizPage.confirmationBlockTSX.${key}`)}
			/>
		</ConfirmationBlock>
	);
}

function convertNumberToDuration(duration: number | null): { hour: number; minute: number } | null {
	if (duration === null) return null;
	if (duration <= 0) return null;
	const hour = Math.floor(duration / 60);
	const minute = duration % 60;

	return { hour, minute };
}

type VerityAssessmentDetailsBlockProps = CommonConfirmationBlockProps & {
	courseName?: string;
	syncWithGradebook?: boolean;
	moduleName?: string;
	title: string;
	description?: string;
	startDate: Date;
	endDate: Date;
	lateSubmissionDate: Date | null;
	duration: number | null;
	numAttemptsAllowed: number;
	allowedFileTypes?: string[];
	assignmentMaxScore?: number;
	scoringStrategy?: string;
};

export function VerityAssessmentDetailsBlock({
	courseName,
	syncWithGradebook,
	moduleName,
	title,
	description,
	startDate,
	endDate,
	lateSubmissionDate,
	duration,
	numAttemptsAllowed,
	allowedFileTypes,
	assignmentMaxScore,
	scoringStrategy,
	...props
}: VerityAssessmentDetailsBlockProps) {
	const multipleDates = spansMultipleDays(startDate, endDate);
	const durationObj = convertNumberToDuration(duration);
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDetails")}
			icon={<AssessmentDetailsIcon />}
			targetPageName="assessmentDetails"
		>
			<ConfirmationRow title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentTitle")} description={title} />
			{description && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDescription")}
					description={description}
					tooltipType="ellipsis"
				/>
			)}
			{courseName && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.course")}
					description={courseName}
					tooltipType="overflow"
				/>
			)}
			{moduleName && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.module")}
					description={moduleName}
					tooltipType="overflow"
				/>
			)}
			{syncWithGradebook !== undefined && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.syncWithGradebook")}
					description={
						syncWithGradebook
							? i18nPackage.t("editQuizPage.confirmationBlockTSX.yes")
							: i18nPackage.t("editQuizPage.confirmationBlockTSX.no")
					}
				/>
			)}
			{typeof assignmentMaxScore === "number" && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assignmentMaxPoints")}
					description={String(assignmentMaxScore)}
				/>
			)}
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDueDate")}
				description={
					multipleDates
						? `${formatLiveSessionDate(startDate)} ${formatLiveSessionTime(startDate)} to ${formatLiveSessionDate(endDate)} ${formatLiveSessionTime(endDate)}`
						: `${formatLiveSessionDate(startDate)} ${formatLiveSessionTime(startDate)} to ${formatLiveSessionTime(endDate)}`
				}
				tooltipType="overflow"
			/>
			{lateSubmissionDate && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.lateSubmission")}
					description={formatLiveSessionDate(lateSubmissionDate)}
				/>
			)}
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentDuration")}
				description={
					durationObj
						? `${durationObj.hour > 0 ? `${durationObj.hour}h ` : ""}${durationObj.minute}m`
						: i18nPackage.t("editQuizPage.confirmationBlockTSX.unlimited")
				}
			/>
			<ConfirmationRow
				title={i18nPackage.t("editQuizPage.confirmationBlockTSX.numAttempts")}
				description={
					numAttemptsAllowed > 0 ? numAttemptsAllowed : i18nPackage.t("editQuizPage.confirmationBlockTSX.unlimited")
				}
			/>
			{scoringStrategy && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.gradeBook")}
					description={scoringStrategy}
				/>
			)}
			{allowedFileTypes && (
				<ConfirmationRow
					title={i18nPackage.t("editQuizPage.confirmationBlockTSX.allowedFileTypes")}
					description={
						allowedFileTypes.length > 0
							? allowedFileTypes.join(", ")
							: i18nPackage.t("editQuizPage.confirmationBlockTSX.allFileTypes")
					}
				/>
			)}
		</ConfirmationBlock>
	);
}

type FileWithErrorMessage = { file: CoreFileItem; errorMessage?: string };

type VerityAssignmentInstructionsBlockProps = {
	files: FileWithErrorMessage[];
	children?: React.ReactNode;
} & CommonConfirmationBlockProps;

export function VerityAssignmentInstructionsBlock({
	files,
	children,
	...props
}: VerityAssignmentInstructionsBlockProps) {
	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.instructions")}
			icon={<AddInstructionIcon />}
			targetPageName="instructions"
		>
			{children}
			{files.length > 0 &&
				files.map((fileWithError, index) => (
					<CoreFileCard
						key={index}
						file={fileWithError.file}
						errorMessage={fileWithError.errorMessage}
						showOpenInNewTab
					/>
				))}
		</ConfirmationBlock>
	);
}
